import { useState, useEffect } from 'react';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Clock, Save, Copy, Timer, CalendarDays } from 'lucide-react';
import {
  AdminPage, FilterChip, PageHeader, Panel, StatusChip, adminButton, adminInput, useAdminPageTitle,
} from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import { BusinessHour, createDefaultHours, useOperationalHoursData, useSaveOperationalHours } from '@/hooks/useOperationalHours';
import { BookingConfirmationPanel } from '@/components/admin/BookingConfirmationPanel';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const BUFFER_OPTIONS = [0, 15, 30, 45, 60, 90, 120];

// Generate time options in 30-minute increments, starting at 8 AM
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const totalHour = (8 + Math.floor(i / 2)) % 24;
  const minutes = i % 2 === 0 ? '00' : '30';
  const time = `${totalHour.toString().padStart(2, '0')}:${minutes}`;
  const displayHours = totalHour === 0 ? 12 : totalHour > 12 ? totalHour - 12 : totalHour;
  const ampm = totalHour < 12 ? 'AM' : 'PM';
  const display = `${displayHours}:${minutes} ${ampm}`;
  return { value: time, display };
});

const OperationalHoursAdmin = () => {
  useAdminPageTitle('Operational Hours');
  const { orgId, loading: orgLoading, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const [hours, setHours] = useState<BusinessHour[]>([]);
  const [bufferMinutes, setBufferMinutes] = useState<number>(30);
  const [dirty, setDirty] = useState(false);
  const { toast } = useToast();

  const { data, isLoading, isError, error } = useOperationalHoursData(orgId, orgLoading);
  const saveMutation = useSaveOperationalHours();

  // Seed local form state when query data arrives
  useEffect(() => {
    if (!data) return;
    setHours(data.hours);
    setBufferMinutes(data.bufferMinutes);
    setDirty(false);
  }, [data]);

  // Surface query errors as toasts
  useEffect(() => {
    if (isError && error) {
      toast({
        title: 'Error',
        description: `Failed to load operational hours: ${(error as Error)?.message ?? String(error)}`,
        variant: 'destructive',
      });
    }
  }, [isError, error, toast]);

  const loading = isLoading || (orgLoading && !orgId);
  const saving = saveMutation.isPending;

  const updateHour = (dayOfWeek: number, field: keyof BusinessHour, value: unknown) => {
    setHours(prev =>
      prev.map(h => h.day_of_week === dayOfWeek ? { ...h, [field]: value } : h)
    );
    setDirty(true);
  };

  const copyHoursTo = (sourceDayOfWeek: number, targetDays: number[]) => {
    const source = hours.find(h => h.day_of_week === sourceDayOfWeek);
    if (!source) return;
    setHours(prev =>
      prev.map(h =>
        targetDays.includes(h.day_of_week)
          ? { ...h, is_open: source.is_open, open_time: source.open_time, close_time: source.close_time }
          : h
      )
    );
    setDirty(true);
  };

  const handleSave = async () => {
    if (!assertCanWrite()) return;
    if (!orgId) return;

    try {
      await saveMutation.mutateAsync({ orgId, hours, bufferMinutes });
      setDirty(false);
      toast({ title: 'Success', description: 'Operational hours saved' });
    } catch (err) {
      console.error('Error saving:', err);
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // When orgId is absent after loading, show empty hours so UI doesn't crash
  const displayHours = hours.length > 0 ? hours : createDefaultHours();

  const openDays = displayHours.filter((h) => h.is_open);
  const timeLabel = (value?: string | null) =>
    TIME_OPTIONS.find((opt) => opt.value === value?.slice(0, 5))?.display ?? value?.slice(0, 5) ?? '';

  return (
    <AdminPage width="narrow" className="pb-28">
      <PageHeader description="Set your opening hours, the gap between jobs, and how online bookings get confirmed." />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_340px] xl:items-start">
        <Panel
          title="Weekly Schedule"
          icon={<Clock className="h-4 w-4 text-muted-foreground" />}
          description="Toggle days on/off and set opening and closing times"
          actions={<StatusChip tone="violet" dot={false}>{openDays.length} of 7 days open</StatusChip>}
          padded={false}
        >
          <div className="adm-divide mt-3 border-t border-border">
            {displayHours.map((hour) => (
              <div
                key={hour.id || hour.day_of_week}
                data-open={hour.is_open}
                className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center md:px-5"
              >
                <div className="flex items-center justify-between gap-4 sm:w-[200px] sm:shrink-0 sm:justify-start">
                  <div className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border text-[11px] font-semibold uppercase',
                        hour.is_open
                          ? 'border-primary/20 bg-secondary text-primary'
                          : 'border-border bg-muted/60 text-muted-foreground',
                      )}
                    >
                      {DAY_NAMES[hour.day_of_week].slice(0, 3)}
                    </span>
                    <span className={cn('w-[84px] text-[13.5px] font-semibold', hour.is_open ? 'text-foreground' : 'text-muted-foreground')}>
                      {DAY_NAMES[hour.day_of_week]}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={hour.is_open}
                      disabled={!canWrite}
                      onCheckedChange={(checked) => updateHour(hour.day_of_week, 'is_open', checked)}
                    />
                    <Label className={cn('w-12 text-[12.5px] font-medium', hour.is_open ? 'text-foreground' : 'text-muted-foreground')}>
                      {hour.is_open ? 'Open' : 'Closed'}
                    </Label>
                  </div>
                </div>

                {hour.is_open && (
                  <div className="grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto] items-center gap-2 sm:ml-auto sm:flex sm:w-auto">
                    <div className="flex min-w-0 items-center gap-2">
                      <Label className="sr-only text-[12px] font-medium text-muted-foreground sm:not-sr-only">From</Label>
                      <Select
                        value={hour.open_time}
                        onValueChange={(value) => updateHour(hour.day_of_week, 'open_time', value)}
                        disabled={!canWrite}
                      >
                        <SelectTrigger className={cn(adminInput, 'w-full min-w-0 sm:w-[112px]')}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.display}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <span className="text-center text-[12px] text-muted-foreground sm:hidden" aria-hidden="true">to</span>
                    <div className="flex min-w-0 items-center gap-2">
                      <Label className="sr-only text-[12px] font-medium text-muted-foreground sm:not-sr-only">To</Label>
                      <Select
                        value={hour.close_time}
                        onValueChange={(value) => updateHour(hour.day_of_week, 'close_time', value)}
                        disabled={!canWrite}
                      >
                        <SelectTrigger className={cn(adminInput, 'w-full min-w-0 sm:w-[112px]')}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.display}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Copy to other days */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button disabled={!canWrite} variant="ghost" size="sm" className={adminButton.iconGhost} title="Copy hours to other days">
                          <Copy />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 rounded-xl p-3" align="end">
                        <p className="adm-label mb-2">Copy to…</p>
                        <div className="mb-3 flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className={cn(adminButton.outline, 'h-8 flex-1 text-[12px]')}
                            onClick={() => copyHoursTo(hour.day_of_week, [1,2,3,4,5])}
                          >
                            Weekdays
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className={cn(adminButton.outline, 'h-8 flex-1 text-[12px]')}
                            onClick={() => copyHoursTo(hour.day_of_week, [0,1,2,3,4,5,6].filter(d => d !== hour.day_of_week))}
                          >
                            All days
                          </Button>
                        </div>
                        <div className="space-y-0.5">
                          {DAY_NAMES.map((name, idx) => {
                            if (idx === hour.day_of_week) return null;
                            return (
                              <label key={idx} className="group flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 hover:bg-muted">
                                <Checkbox
                                  id={`copy-${hour.day_of_week}-to-${idx}`}
                                  onCheckedChange={(checked) => {
                                    if (checked) copyHoursTo(hour.day_of_week, [idx]);
                                  }}
                                />
                                <span className="text-[13px] text-muted-foreground group-hover:text-foreground">{name}</span>
                              </label>
                            );
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                {!hour.is_open && (
                  <div className="adm-stripes hidden h-9 items-center justify-center rounded-lg border border-dashed border-border px-4 text-[12.5px] text-muted-foreground sm:ml-auto sm:flex sm:w-[300px]">
                    <span className="rounded bg-card/90 px-1.5">Closed all day</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>

        <div className="flex flex-col gap-4">
          {/* Instant confirmation vs owner approval (saves on its own) */}
          <BookingConfirmationPanel orgId={orgId} canWrite={canWrite} />

          {/* Buffer Time */}
          <Panel
            title="Buffer Time Between Jobs"
            icon={<Timer className="h-4 w-4 text-muted-foreground" />}
            description="Automatically block time between jobs for travel and preparation. Team members can override this in their settings."
          >
            <p className="adm-label mb-2">Default buffer</p>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Default buffer">
              {BUFFER_OPTIONS.map((mins) => (
                <FilterChip
                  key={mins}
                  active={bufferMinutes === mins}
                  disabled={!canWrite}
                  onClick={() => { setBufferMinutes(mins); setDirty(true); }}
                  className="adm-num disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {mins === 0 ? 'None' : `${mins}m`}
                </FilterChip>
              ))}
            </div>
          </Panel>

          {/* Calendar Preview */}
          <Panel
            title="Calendar Preview"
            icon={<CalendarDays className="h-4 w-4 text-muted-foreground" />}
            description="How operational hours appear on the schedule"
          >
            <div className="grid grid-cols-7 gap-1.5">
              {displayHours.map((hour) => {
                const openHour = parseInt(hour.open_time?.split(':')[0] || '8');
                const closeHour = parseInt(hour.close_time?.split(':')[0] || '18');

                return (
                  <div key={hour.day_of_week} className="min-w-0 space-y-1.5">
                    <p className={cn('text-center text-[11px] font-semibold', hour.is_open ? 'text-foreground' : 'text-muted-foreground')}>
                      {DAY_NAMES[hour.day_of_week].slice(0, 3)}
                    </p>
                    <div className={cn('flex flex-col gap-[3px] rounded-lg p-[3px]', hour.is_open ? 'bg-muted/50' : 'adm-stripes bg-muted/30')}>
                      {[7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map((h) => {
                        const isOpen = hour.is_open && h >= openHour && h < closeHour;
                        return (
                          <div
                            key={h}
                            className={cn('h-2.5 rounded-[3px]', isOpen ? 'bg-primary/70' : 'bg-transparent')}
                            title={`${h}:00 - ${isOpen ? 'Open' : 'Closed'}`}
                          />
                        );
                      })}
                    </div>
                    <p className="adm-num truncate text-center text-[10px] text-muted-foreground">
                      {hour.is_open ? `${openHour}–${closeHour}` : 'Closed'}
                    </p>
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-border pt-3 text-[12px] text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-[3px] bg-primary/70" />
                <span>Open hours</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="adm-stripes h-3 w-3 rounded-[3px] border border-border bg-muted/40" />
                <span>Closed hours</span>
              </div>
            </div>
            {openDays.length > 0 && (
              <p className="mt-3 text-[12px] text-muted-foreground">
                Earliest start <span className="font-semibold text-foreground">{timeLabel(openDays.map((h) => h.open_time).sort()[0])}</span>
                {' · '}latest finish <span className="font-semibold text-foreground">{timeLabel(openDays.map((h) => h.close_time).sort().slice(-1)[0])}</span>
              </p>
            )}
          </Panel>
        </div>
      </div>

      {/* Floating save bar */}
      <div
        className={cn(
          'pointer-events-none fixed bottom-0 left-0 right-0 z-40 px-4 pb-4 transition-transform duration-200 md:left-[248px] md:px-6 lg:px-8',
          dirty ? 'translate-y-0' : 'translate-y-[calc(100%+1rem)]',
        )}
      >
        <div className="adm-panel pointer-events-auto mx-auto flex max-w-[1016px] items-center justify-between gap-4 px-4 py-3 shadow-[var(--adm-shadow-md)]">
          <p className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <span className="h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--adm-sun))]" aria-hidden="true" />
            You have unsaved changes
          </p>
          <Button onClick={handleSave} disabled={!canWrite || saving} size="sm" className={adminButton.primary}>
            {saving ? <Loader2 className="animate-spin" /> : <Save />}
            Save Changes
          </Button>
        </div>
      </div>
    </AdminPage>
  );
};

export default OperationalHoursAdmin;
